import type { CSSProperties, ReactNode } from "react";

import { TERMS_TOP_ROW_CARD_HEIGHT } from "@/components/home/terms-card-spacing";
import { liveHomeCopy as enLiveHomeCopy } from "@/content/pages/en/live-home";
import { cn } from "@/lib/utils";

import type { TermsCopy } from "./types";
import type { TermsMotionState } from "./use-terms-card-motion";

export const PRICING_DURATION_MS = 1200;
export const MOCK_ENTRANCE_END = 0.35;
export const FINANCING_START = 0.35;
export const FEE_START = 0.45;
export const TOTAL_START = 0.6;
export const FINANCING_END = 0.6;
export const FEE_END = 0.7;
export const TOTAL_END = 1100 / PRICING_DURATION_MS;

export const PRICING_EASING = "cubic-bezier(0.22, 1, 0.36, 1)";

export function segmentProgress(phase: number, start: number, end = 1): number {
  if (phase <= start) return 0;
  if (phase >= end) return 1;
  return (phase - start) / (end - start);
}

function cubicBezierEasing(
  p1x: number,
  p1y: number,
  p2x: number,
  p2y: number
): (t: number) => number {
  const NEWTON_ITERATIONS = 4;

  function sampleCurveX(t: number) {
    return (
      ((1 - 3 * p2x + 3 * p1x) * t + (3 * p2x - 6 * p1x)) * t * t + 3 * p1x * t
    );
  }

  function sampleCurveY(t: number) {
    return (
      ((1 - 3 * p2y + 3 * p1y) * t + (3 * p2y - 6 * p1y)) * t * t + 3 * p1y * t
    );
  }

  function sampleCurveDerivativeX(t: number) {
    return (
      (3 * (1 - 3 * p2x + 3 * p1x) * t + 2 * (3 * p2x - 6 * p1x)) * t + 3 * p1x
    );
  }

  function solveCurveX(x: number) {
    let t = x;
    for (let i = 0; i < NEWTON_ITERATIONS; i += 1) {
      const slope = sampleCurveDerivativeX(t);
      if (slope === 0) break;
      t -= (sampleCurveX(t) - x) / slope;
    }
    return t;
  }

  return (t: number) => {
    if (t <= 0) return 0;
    if (t >= 1) return 1;
    return sampleCurveY(solveCurveX(t));
  };
}

/** Eases normalized playback phase for browser-mock entrance in production playback. */
export const pricingPlaybackEase = cubicBezierEasing(0.22, 1, 0.36, 1);

function resolvePricingPhase(
  motionState: TermsMotionState = "complete",
  phase?: number
): number {
  if (phase !== undefined) return phase;
  if (motionState === "idle") return 0;
  return 1;
}

function shouldApplyMotionStyles(
  motionState: TermsMotionState,
  phase?: number
): boolean {
  return phase !== undefined || motionState !== "complete";
}

function BrowserChrome({ animated }: { animated: boolean }) {
  const dotClassName = "bg-grey-100 size-[15px] rounded-full lg:size-[18px]";

  if (animated) {
    // Animated card uses semantic list chrome for role="list" tests and a11y;
    // static card keeps decorative aria-hidden spans to match the Figma reference.
    return (
      <ul className="flex gap-2" role="list">
        <li className={dotClassName} aria-hidden />
        <li className={dotClassName} aria-hidden />
        <li className={dotClassName} aria-hidden />
      </ul>
    );
  }

  return (
    <div className="flex gap-2" aria-hidden>
      <span className={dotClassName} />
      <span className={dotClassName} />
      <span className={dotClassName} />
    </div>
  );
}

export interface PricingBrowserMockProps {
  copy?: TermsCopy;
  financingAmount?: ReactNode;
  feeAmount?: ReactNode;
  totalAmount?: ReactNode;
  motionState?: TermsMotionState;
  phase?: number;
  /** When set, overrides entrance segment progress (e.g. eased production playback). */
  entranceProgress?: number;
}

export function PricingBrowserMock({
  copy = enLiveHomeCopy.terms,
  financingAmount,
  feeAmount,
  totalAmount,
  motionState = "complete",
  phase,
  entranceProgress,
}: PricingBrowserMockProps) {
  const resolvedPhase = resolvePricingPhase(motionState, phase);
  const applyMotionStyles = shouldApplyMotionStyles(motionState, phase);
  const resolvedEntranceProgress =
    entranceProgress ?? segmentProgress(resolvedPhase, 0, MOCK_ENTRANCE_END);

  const mockStyle: CSSProperties | undefined = applyMotionStyles
    ? {
        transform: `translateY(${80 * (1 - resolvedEntranceProgress)}px)`,
        opacity: resolvedEntranceProgress,
      }
    : undefined;

  const rows = [
    {
      label: copy.mockFinancingAmountLabel,
      value: financingAmount ?? copy.mockFinancingAmount,
    },
    { label: copy.mockFeeLabel, value: feeAmount ?? copy.mockFeeAmount },
  ];

  return (
    <div
      data-testid={applyMotionStyles ? "pricing-browser-mock" : undefined}
      className="bg-grey-0 absolute top-[162px] left-[30px] w-[323px] max-w-none translate-x-0 overflow-hidden rounded-[18px] p-[17px] shadow-[-18px_0px_55px_0px_rgba(0,0,0,0.07)] lg:top-[186px] lg:left-1/2 lg:w-[484px] lg:max-w-[calc(100%-32px)] lg:-translate-x-1/2 lg:rounded-[27px] lg:p-5"
      style={mockStyle}
    >
      <div className="flex flex-col gap-[17px] lg:gap-5">
        <BrowserChrome animated={applyMotionStyles} />
        <div className="bg-grey-50 rounded-[12px] p-[28px] lg:rounded-[14px] lg:p-[34px]">
          <dl className="flex flex-col">
            {rows.map(({ label, value }) => (
              <div
                key={label}
                className="border-grey-100 flex items-center justify-between border-b py-2.5"
              >
                <dt className="text-grey-600 text-[14px] leading-[1.4] tracking-[-0.28px] lg:text-[16px] lg:tracking-[-0.02em]">
                  {label}
                </dt>
                <dd className="text-grey-600 text-[20px] leading-[1.2] tracking-[-0.02em] lg:text-[24px]">
                  {value}
                </dd>
              </div>
            ))}
            <div className="flex items-center justify-between pt-4 lg:pt-5">
              <dt className="text-grey-600 text-[14px] leading-[1.4] tracking-[-0.28px] lg:text-[16px] lg:tracking-[-0.02em]">
                {copy.mockTotalLabel}
              </dt>
              <dd className="text-grey-950 text-[24px] leading-[1.2] tracking-[-0.04em] lg:text-[30px]">
                {totalAmount ?? copy.mockTotalAmount}
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
}

/** "Simple, transparent pricing" — heading + a browser-mock fee breakdown that
 * bleeds off the bottom of the card. Pre-Rive static reference (Figma 6055:30126). */
export function StaticPricingCard({
  copy = enLiveHomeCopy.terms,
}: {
  copy?: TermsCopy;
}) {
  return (
    // Mobile (Figma 4081:22942): fixed 404px card, 30/31px header inset.
    <div
      className={cn(
        "border-grey-100 bg-grey-0 relative overflow-hidden rounded-2xl border",
        TERMS_TOP_ROW_CARD_HEIGHT
      )}
    >
      <div className="flex flex-col gap-3 px-[30px] pt-[31px] lg:px-10 lg:pt-12">
        <h3 className="text-grey-950 text-[20px] leading-[1.3] tracking-[-0.04em] lg:text-[24px] lg:leading-[1.2]">
          {copy.pricingTitle}
        </h3>
        <p className="text-grey-600 max-w-[268px] text-[14px] leading-[1.5] tracking-[-0.28px] lg:max-w-[391px] lg:text-[16px] lg:tracking-[-0.02em]">
          {copy.pricingDescription}
        </p>
      </div>

      {/*
       * Browser-mock card — centred and clipped at the bottom edge.
       *
       * DESIGN PENDING (Liam Duggan, Figma 6055:30127): this static fee table is
       * a placeholder. It is slated to become a 3-stage financing demo ("Tell us
       * about your business" → "Securely connect your platforms" → "Get approved
       * for financing", per the section below) conveying the self-serve story
       * from https://www.linkedin.com/posts/dugganliam_last-week-a-business-started-their-financing-activity-7447627595455692800-ujRt
       * Still undecided whether that lands as a looping video or an interactive
       * UI demo — do not invest further here until design resolves the format.
       */}
      <PricingBrowserMock copy={copy} />
    </div>
  );
}
